import React, { Component, Fragment } from "react";
import { DatePicker, Label, MessageBar, MessageBarType, PrimaryButton } from "office-ui-fabric-react";
import moment from 'moment';
import SchoolMenu from "../SchoolMenu";
import SchoolPageTitle from "../SchoolPageTitle";
import { ICDBPageProps } from "../../utils/ICDBPageProps";
import LoadingCard from "../LoadingCard";
import RolloverConfirmForm from "../../models/rollover/RolloverConfirmForm";

interface RolloverFormPageProps extends ICDBPageProps {
    tenantId: string;
    schoolCode: string;
    academicYear: string;
}

interface RolloverFormPageState {
    loading: boolean;
    saving: boolean;
    rolloverConfirmForm?: RolloverConfirmForm;
}

export default class RolloverFormPage extends Component<RolloverFormPageProps, RolloverFormPageState> {

    constructor(props: RolloverFormPageProps) {
        super(props);

        this.state = {
            loading: false,
            saving: false,
        }
    }

    async componentDidMount() {
        if (this.props.tenantId && this.props.schoolCode && this.props.academicYear) {
            await this.loadForm();
        }
    }

    private getTitle() {
        var academicYearInt = parseInt(this.props.academicYear);
        return `Rollover ${academicYearInt}`;
    }

    async loadForm() {
        try {
            this.setState({ loading: true });
            const rolloverConfirmForm = await this.props.apiService.getRolloverConfirmForm(this.props.tenantId, this.props.schoolCode, this.props.academicYear);
            this.setState({ loading: false, rolloverConfirmForm });
        }
        catch (ex) {
            this.props.messageBar.error(ex.message);
            this.setState({ loading: false });
        }
    }

    async saveForm() {
        if (this.state.rolloverConfirmForm !== undefined && this.state.saving === false && this.state.rolloverConfirmForm.customerConfirmed !== true) {
            try {
                this.setState({ saving: true });
                await this.props.apiService.saveRolloverConfirmForm(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.state.rolloverConfirmForm);
                const rolloverConfirmForm = await this.props.apiService.getRolloverConfirmForm(this.props.tenantId, this.props.schoolCode, this.props.academicYear);
                this.setState({ saving: false, rolloverConfirmForm });
                this.props.messageBar.success("You have successfully confirmed");
            }
            catch (ex) {
                this.props.messageBar.error(ex.message);
                this.setState({ saving: false });
            }
        }
    }

    private getConfirmButton() {
        
        const supportEmail = "support@clouddesignbox.co.uk";
        
        if (this.state.rolloverConfirmForm!.customerConfirmed === true) {
            return <Fragment>
                <MessageBar className={"m-top-1rem"} messageBarType={MessageBarType.info}>
                    <strong>Dates have been confirmed</strong>
                    <div>If you need to make any further changes, please contact support - <a href={`mailto:${supportEmail}`}>{supportEmail}</a></div>
                </MessageBar>
            </Fragment>
        }
        else {
            return <PrimaryButton
                className={"m-top-1rem"}
                iconProps={{ iconName: "save" }}
                text="Save"
                disabled={this.state.saving || this.state.rolloverConfirmForm!.customerConfirmed}
                onClick={() => this.saveForm()} />
        }
    }

    private buildForm(): JSX.Element {
        if (this.state.loading || this.state.rolloverConfirmForm === undefined) {
            return <LoadingCard />;
        }
        else {
            return <Fragment>
                <div className="card card">
                    <div className="card__header">
                        <span className={"card__title"}>{this.getTitle()}</span>
                    </div>
                    <div className={"card__content"}>
                        <DatePicker
                            className="card__input"
                            label={"Date new classes will be ready"}
                            value={moment(this.state.rolloverConfirmForm.rolloverDate).toDate()}
                            disabled={this.state.saving || this.state.rolloverConfirmForm.customerConfirmed}
                            minDate={moment().toDate()}
                            onSelectDate={(selectedDate?: Date | undefined | null) => this.setState({
                                rolloverConfirmForm: {
                                    ...this.state.rolloverConfirmForm!,
                                    rolloverDate: moment(selectedDate!).utc(true).format()
                                }
                            })}
                            placeholder="Select a date..." />
                        <MessageBar messageBarType={MessageBarType.warning}>
                            <div>Please make sure your <strong>MIS timetable is available before this date</strong></div>
                            <div>We recommend having your new class teams created early in the summer - even if your academic year doesn’t begin until September. <strong>Student memberships can be added at a later date</strong></div>
                        </MessageBar>

                        <DatePicker
                            className="card__input"
                            label={"Date to add student memberships"}
                            value={moment(this.state.rolloverConfirmForm.addStudentMembershipsDate).toDate()}
                            disabled={this.state.saving || this.state.rolloverConfirmForm.customerConfirmed}
                            minDate={moment().toDate()}
                            onSelectDate={(selectedDate?: Date | undefined | null) => this.setState({
                                rolloverConfirmForm: {
                                    ...this.state.rolloverConfirmForm!,
                                    addStudentMembershipsDate: moment(selectedDate!).utc(true).format()
                                }
                            })}
                            placeholder="Select a date..." />
                        <MessageBar messageBarType={MessageBarType.warning}>
                            Student memberships will be added on this date or in some cases <strong>up to 24 hours before this date</strong>.
                        </MessageBar>

                        <Label className="card__input">
                            Summary
                        </Label>
                        <MessageBar messageBarType={MessageBarType.success}>
                            <div>Your mis data needs to be ready at least 24 hours before <strong>{moment(this.state.rolloverConfirmForm.rolloverDate).format('MMMM Do YYYY')}</strong></div>
                            <div>New classes will be ready for <strong>{moment(this.state.rolloverConfirmForm.rolloverDate).format('MMMM Do YYYY')}</strong></div>
                            <div>Students will be added to classes for <strong>{moment(this.state.rolloverConfirmForm.addStudentMembershipsDate).format('MMMM Do YYYY')}</strong></div>
                        </MessageBar>

                        {this.getConfirmButton()}
                    </div>
                </div>
            </Fragment>
        }
    }

    render() {
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title={this.getTitle()} reverse={true} />
                <SchoolMenu prefix={this.getTitle()} />
                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    {this.buildForm()}
                </div>
            </Fragment>
        );
    }
}
